import { Component, DestroyRef, HostListener, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, debounceTime } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { formatDate } from '@angular/common';
import { MainLayoutComponent } from '../../_layouts/main-layout/main-layout.component';
import { Customer, CustomerOverview, CustomerPage, CustomerSummary } from '../../_interfaces/customer';
import { UserService } from '../../_services/users.service';
import { OrderService } from '../../_services/order.service';
import { AlertService } from '../../_services/alert.service';
import { userRoles } from '../../../Shared/_constants/constants';
import { PaginatorPagesDirective } from '../../../Shared/_directives/paginator-pages.directive';

type Sort = 'NEWEST' | 'NAME' | 'MOST_ORDERS' | 'HIGHEST_SPEND' | 'RECENT_ORDER';
type Activity = 'ALL' | 'ORDERED' | 'NEVER' | 'INACTIVE';

// Customers and trade accounts: a server-paged, searchable list with what
// matters about each one (orders, spend, last order), and a slide-over panel
// with their details, recent orders and addresses. Creating and editing happen
// on their own page (/customers/create, /customers/edit/:id), which gets the
// list's search / filters / sort / page so Previous / Next follow the list
// and Back returns to it as it was.

// The list as it stands, carried in the address to the edit page and back.
export interface CustomerListState {
  role?: string;
  q?: string;
  tier?: string;
  activity?: string;
  sort?: string;
  page?: string;
  size?: string;
}
@Component({
    selector: 'app-customers',
    imports: [PaginatorPagesDirective, MainLayoutComponent, CommonModule, FormsModule, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MatPaginatorModule],
    templateUrl: './customers.component.html',
    styleUrl: './customers.component.css'
})
export class CustomersComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  readonly Math = Math;
  private search$ = new Subject<void>();

  role: 'customer' | 'trader' = 'customer';
  keyword = '';
  userType = 'ALL';
  activity: Activity = 'ALL';
  sort: Sort = 'NEWEST';

  customers: Customer[] = [];
  count = 0;
  pageNumber = 0;
  itemsPerPage = 10;
  summary: CustomerSummary | null = null;
  loading = true;
  loadError = false;
  private loadToken = 0;

  selectedId: number | null = null;
  detail: CustomerOverview | null = null;
  detailLoading = false;

  readonly types = ['NONMEMBER', 'BRONZE', 'SILVER', 'GOLD', 'PLATINUM'];
  readonly sortOptions: { key: Sort; label: string }[] = [
    { key: 'NEWEST', label: 'Newest accounts' },
    { key: 'NAME', label: 'Name A-Z' },
    { key: 'MOST_ORDERS', label: 'Most orders' },
    { key: 'HIGHEST_SPEND', label: 'Highest spend' },
    { key: 'RECENT_ORDER', label: 'Ordered most recently' },
  ];
  readonly activityOptions: { key: Activity; label: string }[] = [
    { key: 'ALL', label: 'Everyone' },
    { key: 'ORDERED', label: 'Has ordered' },
    { key: 'NEVER', label: 'Never ordered' },
    { key: 'INACTIVE', label: 'Deactivated accounts' },
  ];

  constructor(
    private router: Router,
    private userService: UserService,
    private orderService: OrderService,
    private alertService: AlertService,
  ) {}

  ngOnInit(): void {
    // Search as you type; Enter searches straight away.
    this.search$.pipe(debounceTime(350), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.applyFilters());
    // Back from a customer's page: the list as it was.
    const qp = this.route.snapshot.queryParamMap;
    if (qp.get('role') === 'trader') this.role = 'trader';
    if (qp.get('q')) this.keyword = qp.get('q')!;
    if (qp.get('tier')) this.userType = qp.get('tier')!;
    if (qp.get('activity')) this.activity = qp.get('activity') as Activity;
    if (qp.get('sort')) this.sort = qp.get('sort') as Sort;
    if (qp.get('page')) this.pageNumber = Math.max(0, Number(qp.get('page')) || 0);
    if (qp.get('size')) this.itemsPerPage = Math.max(1, Number(qp.get('size')) || 10);
    this.load();
    const open = Number(qp.get('selected'));
    if (open > 0) this.select({ userid: open } as Customer);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.selectedId) this.closeDetail();
  }

  get listState(): CustomerListState {
    const s: CustomerListState = { role: this.role };
    if (this.keyword.trim()) s.q = this.keyword.trim();
    if (this.userType !== 'ALL') s.tier = this.userType;
    if (this.activity !== 'ALL') s.activity = this.activity;
    if (this.sort !== 'NEWEST') s.sort = this.sort;
    if (this.pageNumber) s.page = String(this.pageNumber);
    if (this.itemsPerPage !== 10) s.size = String(this.itemsPerPage);
    return s;
  }

  load(): void {
    const token = ++this.loadToken;
    this.loading = true;
    this.loadError = false;
    this.userService
      .getCustomersPaged(
        {
          pageSize: this.itemsPerPage,
          pageIndex: this.pageNumber,
          userType: this.userType,
          keywordSearch: this.keyword.trim(),
          activity: this.activity,
          sort: this.sort,
        },
        this.role === 'customer' ? userRoles.CUSTOMER : userRoles.TRADER,
      )
      .subscribe({
        next: (response: CustomerPage) => {
          if (token !== this.loadToken) return;
          this.customers = response?.users ?? [];
          this.count = response?.count ?? 0;
          this.summary = response?.summary ?? null;
          this.loading = false;
        },
        error: (error) => {
          if (token !== this.loadToken) return;
          this.loading = false;
          this.loadError = true;
          this.alertService.alert(error?.error?.message || 'Could not load customers', 'error');
        },
      });
  }

  // ---- filters ----
  onSearchInput(): void {
    this.search$.next();
  }

  clearSearch(): void {
    this.keyword = '';
    this.applyFilters();
  }

  applyFilters(): void {
    this.pageNumber = 0;
    this.load();
  }

  setRole(role: 'customer' | 'trader'): void {
    if (this.role === role) return;
    this.role = role;
    this.userType = 'ALL';
    this.closeDetail();
    this.applyFilters();
  }

  setType(type: string): void {
    this.userType = type;
    this.applyFilters();
  }

  get filtersActive(): boolean {
    return !!this.keyword.trim() || this.userType !== 'ALL' || this.activity !== 'ALL';
  }

  resetFilters(): void {
    this.keyword = '';
    this.userType = 'ALL';
    this.activity = 'ALL';
    this.applyFilters();
  }

  typeCount(type: string): number {
    return this.summary?.byType?.[type] ?? 0;
  }

  updateList(event: PageEvent): void {
    if (this.itemsPerPage !== event.pageSize) {
      this.itemsPerPage = event.pageSize || 10;
      this.pageNumber = 0;
    } else {
      this.pageNumber = event.pageIndex;
    }
    this.load();
  }

  // ---- display helpers ----
  fullName(c: { firstname?: string; lastname?: string; useremail?: string }): string {
    return `${c.firstname ?? ''} ${c.lastname ?? ''}`.replace(/\s+/g, ' ').trim() || c.useremail || 'Unnamed';
  }

  initials(c: { firstname?: string; lastname?: string; useremail?: string }): string {
    const a = (c.firstname || c.useremail || '?').trim()[0] ?? '?';
    const b = (c.lastname || '').trim()[0] ?? '';
    return (a + b).toUpperCase();
  }

  typeClass(type: string): string {
    switch (type) {
      case 'BRONZE': return 'bg-orange-100 text-orange-800';
      case 'SILVER': return 'bg-slate-200 text-slate-700';
      case 'GOLD': return 'bg-amber-200 text-amber-900';
      case 'PLATINUM': return 'bg-violet-100 text-violet-800';
      default: return 'bg-neutral-100 text-neutral-500';
    }
  }

  typeLabel(type: string): string {
    return type === 'NONMEMBER' ? 'Non member' : type.charAt(0) + type.slice(1).toLowerCase();
  }

  // "3 days ago" style for the last order; older than a year shows the month.
  ago(iso?: string | null): string {
    if (!iso) return 'Never';
    const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
    if (days < 1) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 31) return `${days} days ago`;
    if (days < 365) return `${Math.round(days / 30)} month${Math.round(days / 30) === 1 ? '' : 's'} ago`;
    return formatDate(iso, 'MMM yyyy', 'en');
  }

  // Ordered in the last 31 days (the green dot on their avatar).
  recentlyOrdered(c: Customer): boolean {
    return !!c.lastOrderDate && Date.now() - new Date(c.lastOrderDate).getTime() < 31 * 86_400_000;
  }

  statusClass(status: string): string {
    switch (status) {
      case 'COMPLETED': return 'bg-emerald-100 text-emerald-800';
      case 'PROCESSING': return 'bg-sky-100 text-sky-800';
      case 'PENDING': case 'PENDING COLLECTION': return 'bg-amber-100 text-amber-800';
      case 'CANCELLED': case 'VOID': case 'FAILED': return 'bg-neutral-100 text-neutral-500';
      case 'REFUNDED': case 'PARTIALLY REFUNDED': return 'bg-rose-100 text-rose-700';
      default: return 'bg-orange-100 text-orange-800';
    }
  }

  mapsUrl(a: { address1?: string; address2?: string; city?: string; postCode?: string; country?: string }): string {
    const q = [a.address1, a.address2, a.city, a.postCode, a.country].filter(Boolean).join(', ');
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(q);
  }

  async copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      this.alertService.alert('Copied', 'success');
    } catch {
      this.alertService.alert('Could not copy', 'error');
    }
  }

  // ---- view panel ----
  select(c: Customer): void {
    if (this.selectedId === c.userid) return;
    this.selectedId = c.userid;
    this.detailLoading = true;
    this.userService.getCustomerOverview(c.userid).subscribe({
      next: (d: CustomerOverview) => {
        if (this.selectedId !== c.userid) return;
        this.detail = d;
        this.detailLoading = false;
      },
      error: (error) => {
        this.detailLoading = false;
        this.alertService.alert(error?.error?.message || 'Could not load the customer', 'error');
      },
    });
  }

  closeDetail(): void {
    this.selectedId = null;
    this.detail = null;
  }

  // Their orders on the Sales page (all dates).
  viewOrders(c: { useremail: string }): void {
    this.orderService.setSearchParams({ email: c.useremail, dtFrom: '2020-01-01', dtTo: formatDate(new Date(), 'yyyy-MM-dd', 'en'), status: 'ALL' });
    this.router.navigate(['/sales']);
  }

  openOrder(orderNo: string): void {
    this.router.navigate(['/sales', orderNo]);
  }

  // ---- create / edit (own page) ----
  onCreateCustomer(): void {
    this.router.navigate(['/customers/create'], { queryParams: this.listState });
  }

  onEditClick(c: { userid: number }): void {
    this.router.navigate(['/customers/edit', c.userid], { queryParams: this.listState });
  }

  // Share of the biggest spender on this page, for the spend bars.
  get maxSpend(): number {
    return Math.max(1, ...this.customers.map((c) => c.totalSpent || 0));
  }
}
